import { useEffect } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import Breadcrumbs from '@/pages/Blog/components/Breadcrumbs';
import JsonLd, { breadcrumbSchema } from '@/components/base/JsonLd';
import KnowledgeCatalogue from './components/KnowledgeCatalogue';
import useSeo, { SITE_URL, OG_IMAGE } from '@/hooks/useSeo';
import {
  knowledgeIntro,
  knowledgeTopics,
  knowledgeGroups,
} from '@/mocks/knowledgeCatalogue';

const pageUrl = `${SITE_URL}/knowledge`;

const faqPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${pageUrl}#faq`,
  url: pageUrl,
  name: 'TNT Coach AI Knowledge Hub',
  inLanguage: 'en-US',
  mainEntity: knowledgeGroups.flatMap((group) =>
    group.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question.replace(/^\d+\.\s*/, ''),
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    }))
  ),
};

const breadcrumbStructuredData = breadcrumbSchema([
  { name: 'Home', url: `${SITE_URL}/` },
  { name: 'Knowledge Hub', url: pageUrl },
]);

export default function KnowledgePage() {
  useSeo({
    title: 'TNT Coach AI Knowledge Hub: 40 Self-Coaching Answers',
    description:
      'Answers to 40 common questions about TNT Coach AI: what it is, the eight coaching styles, features, habits, goals, pricing, access and safety limits.',
    canonical: pageUrl,
    url: pageUrl,
    image: OG_IMAGE,
  });

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 120);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, []);

  const scrollToTopic = (id: string, e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    window.history.replaceState(null, '', `#${id}`);
  };

  return (
    <div className="min-h-screen bg-midnight">
      <JsonLd data={breadcrumbStructuredData} />
      <JsonLd data={faqPageSchema} />
      <Navbar />

      <main>
        {/* Header */}
        <section className="pt-32 pb-10 md:pb-12">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Knowledge Hub' }]} />
              <p className="text-champagne-gold text-xs font-semibold tracking-[0.18em] uppercase mb-3">
                {knowledgeIntro.eyebrow}
              </p>
              <h1
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-warm-white leading-tight mb-5"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                TNT Coach AI Knowledge Hub
              </h1>
              <p className="text-silver-grey text-sm md:text-base leading-relaxed max-w-3xl mb-4">
                {knowledgeIntro.description}
              </p>
              <p className="text-silver-grey/80 text-sm leading-relaxed max-w-3xl">
                Every question below is answered in plain language — what the app does, how the
                coaching styles differ, what it can help with, how pricing works, and where its
                limits are.
              </p>
              <p className="text-silver-grey/60 text-sm leading-relaxed mt-4">
                Main website:{' '}
                <a
                  href="https://topnotchtechnologies.net/"
                  className="text-champagne-gold underline underline-offset-4 hover:opacity-80 transition-opacity duration-300"
                >
                  topnotchtechnologies.net
                </a>{' '}
                <span className="text-taupe-grey">|</span> Coaching app:{' '}
                <a
                  href="https://tnt-coach-ai.base44.app/"
                  className="text-champagne-gold underline underline-offset-4 hover:opacity-80 transition-opacity duration-300"
                >
                  TNT Coach AI
                </a>
              </p>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="border-t border-royal-navy/30" />
        </div>

        {/* Catalogue */}
        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <nav aria-label="Knowledge hub topics" className="flex flex-wrap gap-2.5 mb-10">
                {knowledgeTopics.map((topic) => (
                  <a
                    key={topic.id}
                    href={`#${topic.id}`}
                    onClick={(e) => scrollToTopic(topic.id, e)}
                    className="px-3.5 py-2 rounded-full border border-royal-navy/50 bg-royal-navy/20 text-silver-grey hover:text-champagne-gold hover:border-champagne-gold/60 transition-colors duration-300 text-sm whitespace-nowrap cursor-pointer"
                  >
                    {topic.label}
                  </a>
                ))}
              </nav>

              <KnowledgeCatalogue />
            </div>
          </div>
        </section>

        {/* Internal links + CTA */}
        <section className="pb-20">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto rounded-xl border border-royal-navy/40 bg-royal-navy/10 p-6 md:p-10">
              <h2
                className="text-xl md:text-2xl font-semibold text-warm-white mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Ready to try it yourself?
              </h2>
              <p className="text-silver-grey text-sm md:text-base leading-relaxed mb-6">
                Describe what you are stuck on, pick a coaching style, and get a practical next step
                in seconds. No session bookings, no waiting.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="https://tnt-coach-ai.base44.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full gold-gradient text-midnight font-semibold text-sm hover:opacity-90 transition-opacity duration-300 whitespace-nowrap"
                >
                  Try TNT Coach AI
                  <i className="ri-arrow-right-line" />
                </a>
                <a
                  href="/blog"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-royal-navy/50 bg-royal-navy/20 text-silver-grey hover:text-champagne-gold hover:border-champagne-gold/60 transition-colors duration-300 text-sm whitespace-nowrap"
                >
                  Read the coaching blog
                  <i className="ri-article-line" />
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-royal-navy/50 bg-royal-navy/20 text-silver-grey hover:text-champagne-gold hover:border-champagne-gold/60 transition-colors duration-300 text-sm whitespace-nowrap"
                >
                  Ask us a question
                  <i className="ri-question-line" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <FooterSection />
    </div>
  );
}